import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { ClaimFolder } from "@/api/generated/client/models/ClaimFolder";

interface EditFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder: ClaimFolder | null;
  onSave: (data: { name: string; color: string }) => Promise<void>;
  isLoading: boolean;
}

export function EditFolderDialog({
  open,
  onOpenChange,
  folder,
  onSave,
  isLoading,
}: EditFolderDialogProps) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#3B82F6");

  // Sync local form state when the folder being edited changes
  useEffect(() => {
    if (folder) {
      setName(folder.name);
      setColor(folder.color ?? "#3B82F6");
    }
  }, [folder]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await onSave({ name: name.trim(), color });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Folder</DialogTitle>
          <DialogDescription>
            Update the folder name or color. Claims assigned to it stay assigned.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-folder-name">Folder Name *</Label>
            <Input
              id="edit-folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Acme Roofing"
              maxLength={100}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-folder-color">Color</Label>
            <div className="flex gap-2 items-center">
              <Input
                id="edit-folder-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-20 h-10 cursor-pointer"
                disabled={isLoading}
              />
              <Input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#3B82F6"
                className="flex-1"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || !name.trim()}>
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
